import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { app, getFirebaseDatabase } from "./authHandler";
import { ref, set } from "firebase/database";
import { getAuth } from "firebase/auth";

export const requestNotificationPermission = async (app, role = 'financier') => {
    try {
        console.log(`Requesting notification permission for ${role}...`);
        
        if (Capacitor.getPlatform() !== 'web') {
            return await requestNativePermission(role);
        } else {
            return await requestWebPermission(app, role);
        }
    } catch (error) {
        console.error('An error occurred while retrieving token. ', error);
    }
};

const requestNativePermission = async (role) => {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
        console.log('User denied native push notification permission');
        return;
    }

    await PushNotifications.register();
    
    PushNotifications.addListener('registration', async (token) => {
        console.log('Native Push registration success, token: ' + token.value);
        await saveTokenToDatabase(token.value, role);
    });

    PushNotifications.addListener('registrationError', (error) => {
        console.error('Error on native push registration: ' + JSON.stringify(error));
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('Native Push received: ' + JSON.stringify(notification));
    });
};

const requestWebPermission = async (app, role) => {
    const permission = await Notification.requestPermission();
        
        if (permission === 'granted') {
            console.log('Notification permission granted.');
            const messaging = getMessaging(app);
            
            // To get a token, we must register the service worker with the config params
            const config = {
                apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
                authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
                databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
                projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
                storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
                messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
                appId: process.env.REACT_APP_FIREBASE_APP_ID,
                measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
            };
            
            const configParam = encodeURIComponent(JSON.stringify(config));
            const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?config=${configParam}`);
            
            const currentToken = await getToken(messaging, { 
                vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY, // User needs to add this in .env
                serviceWorkerRegistration: registration 
            });

            if (currentToken) {
                console.log('FCM Token retrieved:', currentToken);
                await saveTokenToDatabase(currentToken, role);
                
                // Handle foreground messages
                onMessage(messaging, (payload) => {
                    console.log('Message received in foreground: ', payload);
                    // Force a system notification even when the app is in the foreground
                    if (payload.notification) {
                        navigator.serviceWorker.ready.then(registration => {
                            registration.showNotification(payload.notification.title, {
                                body: payload.notification.body,
                                icon: payload.notification.image || '/logo192.png'
                            });
                        });
                    }
                });
                
                return currentToken;
            } else {
                console.log('No registration token available. Request permission to generate one.');
            }
        } else {
            console.log('Unable to get permission to notify.');
        }
};

const saveTokenToDatabase = async (token, role) => {
    const auth = getAuth();
    if (!auth.currentUser) return;
    
    const db = getFirebaseDatabase();
    
    if (role === 'customer') {
        const phone = auth.currentUser.phoneNumber;
        if (phone) {
            const tokenRef = ref(db, `global_customers/${phone}/fcmToken`);
            await set(tokenRef, token);
            console.log('Token saved for customer phone:', phone);
        }
    } else {
        const tokenRef = ref(db, `users/${auth.currentUser.uid}/fcmToken`);
        await set(tokenRef, token);
        console.log('Token saved to database for financier:', auth.currentUser.uid);
    }
};
